import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useContext } from "react";
import Button from "../../Button";
import Close from "../../Icons/Close";
import Loader from "../../Loader";
import * as userService from "../../../services/userService";
import { reportNewsPosts } from "../../../services/newsService";
import { StoreContext } from "../../../Context/StoreContext";
import LimitedTextarea from "../../LimitedTextarea";
import { ReportNews } from "../../../types";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  peerUserId: string;
  reportType?: string;
  callId?: string | undefined;
}

export default function ReportModal({
  isOpen,
  setIsOpen,
  peerUserId,
  reportType = "call",
  callId = undefined,
}: Props) {
  const { state } = useContext(StoreContext);
  const [description, setDescription] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const handleDescriptionChange = (text: string) => {
    setDescription(text);
  };

  const {
    auth: { user },
  } = state;
  const handleReport = () => {
    setIsFetching(true);
    const data = callId
      ? {
          reported_user: peerUserId,
          description: description,
          call_id: callId,
        }
      : {
          reported_user: peerUserId,
          description: description,
        };
    userService
      .reportUser(data, user?.token)
      .then(() => {
        setIsFetching(false);
        setIsOpen(false);
        setIsSuccessOpen(true);
      })
      .catch((err) => {
        setIsFetching(false);
        throw err;
      });
  };
  const handleNewsReport = () => {
    if (!callId) return;
    setIsFetching(true);
    const data: ReportNews = {
      news_id: callId,
      reported_user: peerUserId,
      description: description,
      type: reportType,
    };

    reportNewsPosts(user?.token, data)
      .then(() => {
        setIsFetching(false);
        setIsOpen(false);
        setIsSuccessOpen(true);
      })
      .catch((err) => {
        setIsFetching(false);
        throw err;
      });
  };
  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-40 overflow-y-auto"
          onClose={() => setIsOpen(false)}
        >
          <div className="flex md:items-end items-center justify-center min-h-screen pt-4 px-4 pb-0 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-overlay bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block align-bottom bg-white dark:bg-navyGray rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-96 font-ubuntu">
                <div className="bg-white dark:bg-navyGray p-4 border-b border-frescoWhite dark:border-matteGray">
                  <div className="flex justify-between items-center">
                    <p className="font-medium text-lg text-black dark:text-frescoWhite">
                      Report
                    </p>
                    <span
                      className="bg-frescoWhite p-9px rounded-full cursor-pointer"
                      onClick={() => {
                        setIsOpen(false);
                      }}
                    >
                      <Close />
                    </span>
                  </div>
                </div>

                <div>
                  <div className="px-4 pt-2">
                    <div>
                      <p className="text-gray-500 dark:text-gray-300">
                        Please explain why you are reporting this person. We
                        take these very seriously to ensure Vibehut remains a
                        safe community.
                      </p>

                      <LimitedTextarea
                        limit={500}
                        value={description}
                        rows={5}
                        onChange={handleDescriptionChange}
                        placeHolder="Comment Here..."
                      />
                    </div>
                  </div>
                  <div className=" px-4 py-3 flex flex-row-reverse ">
                    {isFetching ? (
                      <Loader />
                    ) : (
                      <Button
                        onClick={
                          reportType === "news"
                            ? handleNewsReport
                            : handleReport
                        }
                        size="vibhut-sm"
                        variant="vibhut-primary"
                      >
                        Submit
                      </Button>
                    )}
                    <div className="mr-3">
                      <Button
                        size="vibhut-sm"
                        variant="vibhut-secondary"
                        onClick={() => setIsOpen(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      <SuccessModal
        isSuccessOpen={isSuccessOpen}
        setIsSuccessOpen={setIsSuccessOpen}
      />
    </>
  );
}

ReportModal.defaultProps = {
  reportType: "call",
  callId: undefined,
};

function SuccessModal({
  isSuccessOpen,
  setIsSuccessOpen,
}: {
  isSuccessOpen: boolean;
  setIsSuccessOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return (
    <>
      <Transition appear show={isSuccessOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-40 overflow-y-auto"
          onClose={() => setIsSuccessOpen(false)}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-overlay bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex flex-row justify-between">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Successfully Reported!
                  </Dialog.Title>
                  <span
                    className="bg-frescoWhite rounded-full p-1 cursor-pointer"
                    onClick={() => setIsSuccessOpen(false)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-600"
                      fill="none"
                      aria-hidden="true"
                      focusable="false"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </span>
                </div>
                <hr className="mt-3" />
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Thank you for your feedback. Our team will be reviewing it
                    shortly.
                  </p>
                  <p className="text-sm text-gray-500">- Vibehut Team🏠</p>
                </div>

                <div className="mt-4 flex flex-row justify-end">
                  <Button
                    size="vibhut-sm"
                    variant="vibhut-primary"
                    onClick={() => setIsSuccessOpen(false)}
                  >
                    Okay
                  </Button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
